<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { Hand } from 'lucide-vue-next'
import { useImageActionStore } from '@/stores/imageActionStore'

const props = defineProps<Props>()

interface Props {
	data: Record<string, { nsfw: boolean, uuid: string, description?: string }[]>
}

const { t } = useI18n()
const areYou18 = shallowRef(false)
const showAgeModal = ref(false)

// Modal personnalisé plus fiable que useConfirm
function requireConfirmationAge() {
	showAgeModal.value = true
}

function confirmAge() {
	areYou18.value = true
	showAgeModal.value = false
}

function rejectAge() {
	showAgeModal.value = false
}

const categories = computed(() => Object.keys(props.data))
const currentCategory = ref<string>('girl')
</script>

<template>
	<div class="w-full max-w-7xl p-4 flex-center flex-col gap-4 mx-auto">
		<!-- Modal personnalisé pour la vérification d'âge -->
		<div
			v-if="showAgeModal"
			class="fixed inset-0 z-50 flex items-center justify-center p-4"
			@click.self="rejectAge"
		>
			<!-- Backdrop -->
			<div class="absolute inset-0 bg-black/50 backdrop-blur-sm" />

			<!-- Modal -->
			<div class="relative bg-slate-800 rounded-2xl border border-slate-700 p-6 max-w-sm w-full mx-4 shadow-2xl">
				<div class="flex items-center flex-col gap-4 text-center">
					<div class="w-16 h-16 flex-center bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
						<Hand class="w-8 h-8" />
					</div>

					<div>
						<h3 class="text-lg font-semibold text-white mb-2">
							{{ t('age_verification.title') }}
						</h3>
						<p class="text-slate-300 text-sm">
							{{ t('age_verification.message') }}
						</p>
					</div>

					<div class="flex gap-3 w-full">
						<Button
							class="button-slate flex-1"
							outlined
							:label="t('age_verification.button.under_18')"
							@click="rejectAge"
						/>
						<Button
							class="button-indigo flex-1"
							:label="t('age_verification.button.over_18')"
							@click="confirmAge"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Header Section -->
		<div class="text-center mb-16 w-full">
			<h1 class="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent mb-6 leading-tight break-words hyphens-auto px-2">
				{{ t('promo.blocks.possibilities.title') }}
				<span class="block text-lg sm:text-xl md:text-2xl lg:text-4xl mt-2 break-words hyphens-auto">{{ t('promo.blocks.possibilities.subtitle') }}</span>
			</h1>

			<p class="text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed px-2 break-words">
				{{ t('promo.blocks.possibilities.description') }}
			</p>
		</div>

		<div class="flex flex-wrap justify-center gap-2 w-full px-2">
			<Button
				v-for="category in categories"
				:key="category"
				class="button-indigo !text-sm sm:!text-base md:!text-lg flex-shrink-0 min-w-0"
				:outlined="category !== currentCategory"
				@click="currentCategory = category"
			>
				<span class="truncate">{{ t(`account_setup.preferences.values.${category}`) }}</span>
			</Button>
		</div>

		<!-- Gallery Grid -->
		<div class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 px-2">
			<TransitionGroup name="list">
				<div
					v-for="(recreate, index) in props.data[currentCategory]"
					:key="index"
					class="group relative w-full min-w-0"
				>
					<!-- Card Container -->
					<div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-slate-700/30 hover:border-purple-500/30 transition-all duration-500 group-hover:scale-[1.02] group-hover:shadow-2xl group-hover:shadow-purple-500/10 w-full">
						<!-- Animated Background Elements -->
						<div class="absolute inset-0 opacity-60">
							<div class="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-2xl transform -translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-700" />
							<div class="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-pink-500/20 to-transparent rounded-full blur-xl transform translate-x-6 translate-y-6 group-hover:scale-125 transition-transform duration-700" />
							<div class="absolute top-1/2 left-1/2 w-20 h-20 bg-gradient-to-r from-blue-500/10 to-transparent rounded-full blur-xl transform -translate-x-1/2 -translate-y-1/2 group-hover:rotate-180 transition-transform duration-1000" />
						</div>

						<!-- Age Verification Overlay -->
						<button
							v-if="!areYou18 && recreate.nsfw"
							class="absolute inset-0 z-30 bg-transparent outline-none border-none cursor-pointer"
							@click="requireConfirmationAge"
						/>

						<!-- Content -->
						<div class="relative z-10 p-4 sm:p-6">
							<!-- Image Container -->
							<div class="relative mb-4 sm:mb-6 overflow-hidden rounded-xl group/image">
								<div class="aspect-[512/704] relative flex-center bg-gradient-to-br from-slate-700 to-slate-800">
									<!-- NSFW Overlay -->
									<div
										v-if="recreate.nsfw && !areYou18"
										class="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-900/80 p-2"
									>
										<div class="text-center">
											<div class="w-12 h-12 sm:w-16 sm:h-16 !hidden sm:!flex flex-center bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto mb-3 sm:mb-4">
												<Hand class="w-6 h-6 sm:w-8 sm:h-8" />
											</div>
											<p class="text-white font-semibold mb-1 text-sm sm:text-base break-words">
												{{ t('promo.blocks.possibilities.18+') }}
											</p>
											<p class="text-slate-300 text-xs sm:text-sm break-words">
												{{ t('promo.blocks.possibilities.verify_age') }}
											</p>
										</div>
									</div>

									<!-- Image -->
									<img
										:src="`https://cloudflare-b2.fapfap-image.workers.dev/fapfap/${recreate.uuid}-min.webp`"
										:class="{
											'blur-lg scale-110': recreate.nsfw && !areYou18,
											'group-hover/image:scale-105': !(recreate.nsfw && !areYou18),
										}"
										class="w-full h-full object-cover transition-all duration-500"
										width="196"
										loading="lazy"
										alt="AI Generated Image"
									>

									<!-- Image Overlay Gradient -->
									<div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
								</div>
							</div>
							<div
								v-if="recreate.description"
								class="text-base font-bold pb-4 ml-2"
							>
								{{ recreate.description }}
							</div>
							<!-- Action Button -->
							<Button
								class="button-purple mx-auto w-full sm:w-auto text-sm sm:text-base"
								:label="t('promo.blocks.possibilities.button')"
								@click="useImageActionStore().clone(recreate.uuid, true)"
							/>
						</div>

						<!-- Shine Effect -->
						<div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
					</div>
				</div>
			</TransitionGroup>
		</div>
	</div>
</template>

<style scoped lang="scss">
@media (max-width: 640px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

}
</style>
